// ============================================================
// BUILD GESTURES - which cells a drag actually means.
//
// Two gestures cover everything a GM does. RECT builds the barracks;
// BRUSH carves the hillside. Both answer the same question - "which
// cells?" - so they are one pure function with a mode, and every tool
// (fill, erase, zone, dig) consumes the answer without caring which
// gesture produced it.
//
// Keeping this out of the pointer handlers is what makes it testable:
// there is no DOM in a rectangle.
//
// Pure. Zero React dependency.
// ============================================================
import { cellKey } from './voxels';
import { zoneCellKey } from './zones';

type Gesture = 'rect' | 'brush' | 'line';

export interface Cell2 {
  x: number;
  z: number;
}

/** Every cell in the rectangle spanned by two corners, inclusive. */
export function rectCells(a: Cell2, b: Cell2): Cell2[] {
  const x0 = Math.min(a.x, b.x);
  const x1 = Math.max(a.x, b.x);
  const z0 = Math.min(a.z, b.z);
  const z1 = Math.max(a.z, b.z);
  const cells: Cell2[] = [];
  for (let x = x0; x <= x1; x++) for (let z = z0; z <= z1; z++) cells.push({ x, z });
  return cells;
}

/**
 * The square a brush of this size covers, centred on a cell.
 *
 * Odd sizes only, so there is always a centre cell under the pointer -
 * an even brush would sit between cells and be impossible to aim.
 */
export function brushCells(centre: Cell2, size = 1): Cell2[] {
  // (size − 1) / 2, so an even size rounds DOWN to the odd one below it:
  // 4 behaves as 3. Rounding up would put the pointer off-centre.
  return brushTipCells(centre, size, 'square');
}

/** Every offset within `reach` of a centre, x outer and z inner - the order every stamp paints in. */
function offsetsWithin(reach: number): [number, number][] {
  const out: [number, number][] = [];
  for (let dx = -reach; dx <= reach; dx++) for (let dz = -reach; dz <= reach; dz++) out.push([dx, dz]);
  return out;
}

/**
 * Fill the gaps a fast drag leaves behind.
 *
 * Pointer events arrive every few pixels at best, and at speed they
 * arrive every few CELLS. Without this a quick stroke paints a dotted
 * line, which reads as the app dropping input.
 */
export function lineCells(from: Cell2, to: Cell2): Cell2[] {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const steps = Math.max(Math.abs(dx), Math.abs(dz));
  if (steps === 0) return [{ ...to }];
  const cells: Cell2[] = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({ x: Math.round(from.x + (dx * i) / steps), z: Math.round(from.z + (dz * i) / steps) });
  }
  return cells;
}

/** Drop duplicates, keeping the first of each. */
export function uniqueCells(cells: Cell2[]): Cell2[] {
  const seen = new Set<string>();
  const out: Cell2[] = [];
  for (const cell of cells) {
    const key = zoneCellKey(cell.x, cell.z);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cell);
  }
  return out;
}

export interface GestureState {
  gesture: Gesture;
  /** Where the drag began. */
  from: Cell2;
  /** Where it is now. */
  to: Cell2;
  /** Cells already touched, for a brush stroke. */
  painted: Cell2[];
  brushSize: number;
  /** Which shape a 'rect' drag lays out. Absent means a plain rectangle. */
  shape?: ShapeKind;
  /** Sides, when the shape is an n-gon. */
  sides?: number;
  /** Just the edge: a room rather than a slab. */
  hollow?: boolean;
  /** The brush's tip. Absent means square. */
  tip?: BrushTip;
  /** How wide a 'line' drag paints. Absent means one cell. */
  thickness?: number;
}

/**
 * The footprint a gesture currently covers.
 *
 * A rect is recomputed from its two corners every time, so dragging back
 * shrinks it. A brush accumulates, because a stroke is the union of
 * everywhere it has been.
 */
export function gestureFootprint(state: GestureState): Cell2[] {
  if (state.gesture === 'rect') {
    return shapeCells(state.shape ?? 'rect', state.from, state.to, {
      sides: state.sides,
      hollow: state.hollow,
    });
  }
  if (state.gesture === 'line') return thickLineCells(state.from, state.to, state.thickness ?? 1);
  return uniqueCells(state.painted);
}

/** Extend a brush stroke to a new cell, filling in any gap. */
export function extendStroke(state: GestureState, to: Cell2): Cell2[] {
  const stepped = lineCells(state.to, to).flatMap((cell) => brushTipCells(cell, state.brushSize, state.tip));
  return uniqueCells([...state.painted, ...stepped]);
}

/**
 * A footprint turned into voxel cells: one layer per level, `layers`
 * deep. This is what builds a volume in a single drag.
 */
export function volumeKeys(footprint: Cell2[], level: number, layers = 1): string[] {
  const keys: string[] = [];
  const count = Math.max(1, Math.floor(layers));
  for (const cell of footprint) {
    for (let i = 0; i < count; i++) keys.push(cellKey(cell.x, level + i, cell.z));
  }
  return keys;
}

/**
 * Cells to REMOVE, counting downward from the top of the column.
 *
 * Erasing has to work top-first. Building upward from the topmost cube
 * would take away the air above a wall and leave the wall - which reads
 * as the erase having silently failed, and is exactly what it looked
 * like when a two-cube platform kept its bottom cube.
 *
 * Stops at the ground: taking earth away is what Dig is for.
 */
export function eraseKeys(footprint: Cell2[], topLevel: number, layers = 1): string[] {
  const keys: string[] = [];
  const count = Math.max(1, Math.floor(layers));
  for (const cell of footprint) {
    for (let i = 0; i < count; i++) {
      const y = topLevel - i;
      if (y < 0) break;
      keys.push(cellKey(cell.x, y, cell.z));
    }
  }
  return keys;
}

/** The same, downward from just under the surface - what digging means. */
export function digKeys(footprint: Cell2[], level: number, layers = 1): string[] {
  const keys: string[] = [];
  const count = Math.max(1, Math.floor(layers));
  // `level` is the first level to remove, and digging goes DOWN from it.
  const top = Math.min(level, -1);
  for (const cell of footprint) {
    for (let i = 0; i < count; i++) keys.push(cellKey(cell.x, top - i, cell.z));
  }
  return keys;
}

/** A footprint as zone cells. */
export function zoneKeys(footprint: Cell2[]): string[] {
  return footprint.map((cell) => zoneCellKey(cell.x, cell.z));
}

// ============================================================
// SHAPES - a rectangle was never the only room worth building.
//
// Every shape answers the same question the gestures do, "which cells?",
// so every one of them is a PREDICATE over the dragged bounding box and
// nothing downstream learns a new word. Filled is the cells that pass;
// hollow is the cells that pass and touch one that does not - which is
// the definition of an edge, and gives a hollow rectangle four walls, a
// hollow ellipse a ring, and a hollow triangle three sides, from one
// piece of code that knows about none of them.
// ============================================================

/** The shapes a drag can lay out. */
export type ShapeKind = 'rect' | 'ellipse' | 'triangle' | 'ngon';

export interface ShapeOptions {
  /** Sides for 'ngon'. Ignored by everything else. Minimum 3. */
  sides?: number;
  /** Just the edge, rather than the whole area. A room instead of a slab. */
  hollow?: boolean;
}

interface Bounds {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  cx: number;
  cz: number;
  rx: number;
  rz: number;
}

function bounds(a: Cell2, b: Cell2): Bounds {
  const x0 = Math.min(a.x, b.x);
  const x1 = Math.max(a.x, b.x);
  const z0 = Math.min(a.z, b.z);
  const z1 = Math.max(a.z, b.z);
  return {
    x0,
    x1,
    z0,
    z1,
    cx: (x0 + x1) / 2,
    cz: (z0 + z1) / 2,
    // Half an extra cell each way: a drag of one cell is a shape of one
    // cell, not an empty set. Radii are never zero.
    rx: (x1 - x0) / 2 + 0.5,
    rz: (z1 - z0) / 2 + 0.5,
  };
}

/** Is this cell inside a regular polygon inscribed in the bounds? */
function insideNgon(x: number, z: number, b: Bounds, sides: number): boolean {
  const n = Math.max(3, Math.round(sides));
  // Point in convex polygon: inside every edge's half-plane. The polygon
  // is built on the bounding ellipse, so it stretches with the drag.
  const vertices = Array.from({ length: n }, (_, i) => {
    // Start at the top, so a triangle points up and a hexagon sits flat.
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    return { x: b.cx + Math.cos(angle) * b.rx, z: b.cz + Math.sin(angle) * b.rz };
  });
  for (let i = 0; i < n; i++) {
    const p = vertices[i];
    const q = vertices[(i + 1) % n];
    // Cross product: negative on the outside for this winding.
    if ((q.x - p.x) * (z - p.z) - (q.z - p.z) * (x - p.x) < -1e-9) return false;
  }
  return true;
}

function predicate(kind: ShapeKind, b: Bounds, sides: number): (x: number, z: number) => boolean {
  switch (kind) {
    case 'ellipse':
      return (x, z) => ((x - b.cx) / b.rx) ** 2 + ((z - b.cz) / b.rz) ** 2 <= 1 + 1e-9;
    case 'triangle':
      // NOT an inscribed regular triangle - that leaves a gap along the
      // bottom of the box, and someone who drags a box expects the shape
      // to fill it. Apex at the top middle, base along the bottom edge.
      return (x, z) => {
        const down = (z - b.z0 + 0.5) / (b.z1 - b.z0 + 1);
        const halfWidth = (down * (b.x1 - b.x0 + 1)) / 2;
        return Math.abs(x - b.cx) <= halfWidth + 1e-9;
      };
    case 'ngon':
      return (x, z) => insideNgon(x, z, b, sides);
    case 'rect':
    default:
      return () => true;
  }
}

/**
 * The cells a shape drag covers.
 *
 * `hollow` keeps only the cells on the edge - those with a neighbour
 * that is not in the shape - so one drag lays out a ROOM: four walls
 * and a floor nobody has to erase.
 */
export function shapeCells(kind: ShapeKind, a: Cell2, b: Cell2, options: ShapeOptions = {}): Cell2[] {
  const box = bounds(a, b);
  const inside = predicate(kind, box, options.sides ?? 6);

  const filled: Cell2[] = [];
  const present = new Set<string>();
  for (let x = box.x0; x <= box.x1; x++) {
    for (let z = box.z0; z <= box.z1; z++) {
      if (!inside(x, z)) continue;
      filled.push({ x, z });
      present.add(`${x},${z}`);
    }
  }
  if (!options.hollow) return filled;
  return outlineCells(filled);
}

/**
 * Just the edge of a set of cells: those with a neighbour outside it.
 *
 * Diagonals count, or a 45° wall comes out with holes a shot could pass
 * through. This is what makes a hollow shape a room - and, separately,
 * what lets a very large drag be PREVIEWED cheaply: the outline of forty
 * thousand cells is a few hundred, and it still looks like the shape it
 * is.
 */
export function outlineCells(cells: Cell2[]): Cell2[] {
  const present = new Set(cells.map((c) => `${c.x},${c.z}`));
  return cells.filter(({ x, z }) => NEIGHBOURS.some(([dx, dz]) => !present.has(`${x + dx},${z + dz}`)));
}

/** The eight cells around one, diagonals included. */
const NEIGHBOURS = offsetsWithin(1).filter(([dx, dz]) => dx || dz);

/** The shape of a brush's tip. */
export type BrushTip = 'square' | 'round' | 'diamond';

/**
 * The cells a brush of this size and tip covers, centred on a cell.
 *
 * A round tip is what you want for a hillside and a square one for a
 * wall, and having only the square meant every organic shape had to be
 * corrected by hand afterwards.
 */
export function brushTipCells(centre: Cell2, size = 1, tip: BrushTip = 'square'): Cell2[] {
  const reach = Math.floor((Math.max(1, size) - 1) / 2);
  const keeps = TIP_KEEPS[tip] ?? TIP_KEEPS.square;
  return offsetsWithin(reach)
    .filter(([dx, dz]) => keeps(dx, dz, reach))
    .map(([dx, dz]) => ({ x: centre.x + dx, z: centre.z + dz }));
}

/** Which offsets each tip keeps: all of the square, a disc, a diamond. */
const TIP_KEEPS: Record<BrushTip, (dx: number, dz: number, reach: number) => boolean> = {
  square: () => true,
  round: (dx, dz, reach) => !(Math.hypot(dx, dz) > reach + 0.5),
  diamond: (dx, dz, reach) => !(Math.abs(dx) + Math.abs(dz) > reach),
};

/**
 * A straight line of a given thickness, at any angle.
 *
 * Walls are the commonest thing on any map and the least well served by
 * a rectangle, which can only ever be axis-aligned.
 */
export function thickLineCells(a: Cell2, b: Cell2, thickness = 1): Cell2[] {
  const spine = lineCells(a, b);
  if (thickness <= 1) return spine;
  // A round tip stamped at every cell of the spine, each cell once.
  return uniqueCells(spine.flatMap((cell) => brushTipCells(cell, thickness, 'round')));
}
